import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useUser } from './user-provider';
import useApi from '../hooks/use-api';
import buildApiRequestConfig from '../api/url-builder';
import routes from '../api/routes';

export type PermissionAction = 'read' | 'create' | 'update' | 'delete';

export interface ResourcePermission {
    canRead: boolean;
    canCreate: boolean;
    canUpdate: boolean;
    canDelete: boolean;
}

interface PermissionsContextType {
    isAdmin: boolean;
    isLoading: boolean;
    permissions: Record<string, ResourcePermission>;
    can: (resource: string, action: PermissionAction) => boolean;
    reload: () => void;
}

const PermissionsContext = createContext<PermissionsContextType>({
    isAdmin: false,
    isLoading: false,
    permissions: {},
    can: (_r, action) => action === 'read',
    reload: () => {},
});

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useUser();
    const [isAdmin, setIsAdmin] = useState(false);
    const [permissions, setPermissions] = useState<Record<string, ResourcePermission>>({});
    const [permResult, setPermConfig] = useApi();
    // Which entityId we've successfully loaded permissions for at least once.
    // Distinguishes the real initial load (isLoading should gate the whole app,
    // e.g. via PermissionGuard) from a background reload() - e.g. after saving
    // role permissions - which must NOT flip isLoading back to true, or every
    // consumer gating on it (like PermissionGuard) unmounts and remounts,
    // wiping their local state for no reason.
    const [loadedForEntityId, setLoadedForEntityId] = useState<number | null>(null);

    const load = useCallback(() => {
        if (!user?.entityId) return;
        setPermConfig(
            buildApiRequestConfig(routes.entityRoles.myPermissions, [{ param: 'entityId', value: user.entityId }])
        );
    }, [user?.entityId, user?.guid]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const data = permResult.data;
        if (!data?.success) return;
        setIsAdmin(!!data.isAdmin);
        setPermissions(data.permissions ?? {});
        setLoadedForEntityId(user?.entityId ?? null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [permResult.data]);

    const isInitialLoad = loadedForEntityId !== (user?.entityId ?? null);

    const can = useCallback((resource: string, action: PermissionAction): boolean => {
        if (isAdmin) return true;
        const perm = permissions[resource];
        if (!perm) return action === 'read';
        const map: Record<PermissionAction, keyof ResourcePermission> = {
            read: 'canRead', create: 'canCreate', update: 'canUpdate', delete: 'canDelete',
        };
        return !!perm[map[action]];
    }, [isAdmin, permissions]);

    return (
        <PermissionsContext.Provider value={{
            isAdmin,
            isLoading: permResult.isLoading && isInitialLoad,
            permissions,
            can,
            reload: load,
        }}>
            {children}
        </PermissionsContext.Provider>
    );
};

export const usePermissions = () => useContext(PermissionsContext);
